<script setup lang="ts">
import { HotelFiltersPanel } from '@apps/main/new-desktop/modules/hotelFilters';
import { HotelDetailsEventSource } from '@apps/main/new-desktop/core/models';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import type { FlightsFilterEvents } from '@apps/main/new-desktop/modules/flightsFilters';
import { FlightsFiltersPanel } from '@apps/main/new-desktop/modules/flightsFilters';
import { FerryFiltersPanel } from '@apps/main/new-desktop/modules/ferryFilters';
import { HotelDetailsPanel } from '@apps/main/new-desktop/modules/hotelDetails';
import { ActivityDetailsPanel } from '@apps/main/new-desktop/modules/activityDetails';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';

import {
  useEditTripStore,
  useTripOverviewDestinationSelectorStore,
  useTripOverviewModalsStore,
  useTripOverviewPanelsStore,
} from '../stores';
import { TripOverviewPanelType } from '../models';

import TripSummaryPanel from './panels/TripSummaryPanel.vue';
import TripPriceSummaryPanel from './panels/TripPriceSummaryPanel.vue';
import {
  ConfigurationPanel,
  ConfigurationStepperPanel,
} from './configurationPanel';

const destinationSelectorStore = useTripOverviewDestinationSelectorStore();
const editTripStore = useEditTripStore();
const tripOverviewModalsStore = useTripOverviewModalsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const editingIngredientStore = useEditingIngredientStore();
const flightsFiltersStore = useFlightsFiltersStore();
const ferryFiltersStore = useFerryFiltersStore();
const statsigEventsStore = useStatsigEventsStore();

function closeConfigurationPanel(): void {
  tripOverviewPanelsStore.closeConfigurationPanel();
  tripOverviewModalsStore.openInitialFerryModalWhenPossible();
}

function onCloseFlightsFiltersPanel(): void {
  tripOverviewPanelsStore.closeFlightsFiltersPanel();
  flightsFiltersStore.clearAllowedFlightIngredientIds();
}

function onApplyAllFlightsFilters(filters: FlightsFilterEvents[]): void {
  flightsFiltersStore.updateAllFilters(filters);
  tripOverviewPanelsStore.closeFlightsFiltersPanel();
  flightsFiltersStore.clearAllowedFlightIngredientIds();
}

function onApplyHotelFilters(): void {
  tripOverviewPanelsStore.applyHotelFilters();
  statsigEventsStore.logTripOverviewFiltersApplied();
}

function onResetHotelFilters(): void {
  tripOverviewPanelsStore.resetHotelFilters();
  statsigEventsStore.logTripOverviewFiltersReset();
}
</script>

<template>
  <ActivityDetailsPanel
    v-if="
      tripOverviewPanelsStore.tripOverviewPanelType ===
        TripOverviewPanelType.ActivityDetails &&
      tripOverviewPanelsStore.activityDetailsData
    "
    :activity-details="tripOverviewPanelsStore.activityDetailsData"
    @close="tripOverviewPanelsStore.closeActivityDetailsPanel()"
  />

  <ConfigurationPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.Configuration
    "
    @close="closeConfigurationPanel"
    @confirm="closeConfigurationPanel"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="editTripStore.changeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
    @update-pax="tripOverviewPanelsStore.updatePax"
  />

  <ConfigurationStepperPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.ConfigurationStepper
    "
    @close="tripOverviewPanelsStore.closeConfigurationStepperPanel"
    @change-trip-order="editTripStore.changeTripOrder"
    @add-destination="destinationSelectorStore.handleConfirmSelection"
    @change-destination="
      destinationSelectorStore.changeDestinationByIngredientId(
        $event.suggestion,
        $event.ingredientId
      )
    "
    @change-number-of-nights="editTripStore.changeNumberOfNights"
    @remove-destination="destinationSelectorStore.removeDestination"
    @update-pax="tripOverviewPanelsStore.updatePax"
  />

  <HotelDetailsPanel
    v-if="
      tripOverviewPanelsStore.tripOverviewPanelType ===
        TripOverviewPanelType.HotelDetails &&
      tripOverviewPanelsStore.hotelDetailsData
    "
    :hotel-details="tripOverviewPanelsStore.hotelDetailsData"
    :source="HotelDetailsEventSource.TripOverview"
    @close="tripOverviewPanelsStore.closeHotelDetailsPanel()"
    @stay-selected="tripOverviewPanelsStore.selectStay"
    @toggle-heart="tripOverviewPanelsStore.toggleHeart"
  />

  <TripPriceSummaryPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.TripPriceSummary
    "
    @close="tripOverviewPanelsStore.closeTripPriceSummaryPanel"
  />
  <TripSummaryPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.TripSummary
    "
    @close="tripOverviewPanelsStore.closeTripSummaryPanel"
  />

  <HotelFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.HotelFilters
    "
    :disable-hotel-name-filter="true"
    :ingredient="editingIngredientStore.editingStayIngredient"
    @close="tripOverviewPanelsStore.closeHotelFiltersPanel()"
    @update-filters="tripOverviewPanelsStore.updateHotelFilters($event)"
    @apply-filters="onApplyHotelFilters"
    @reset-filters="onResetHotelFilters"
  />

  <FlightsFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.FlightsFilters
    "
    :header-text="$t('filter.preferences')"
    :data="flightsFiltersStore.filters"
    :should-update-on-click="true"
    @apply-all-filters="onApplyAllFlightsFilters"
    @close="onCloseFlightsFiltersPanel"
    @reset-all-filters="flightsFiltersStore.resetFlightFilters"
  />

  <FerryFiltersPanel
    :is-open="
      tripOverviewPanelsStore.tripOverviewPanelType ===
      TripOverviewPanelType.FerryFilters
    "
    :ingredient-id="editingIngredientStore.editingFerryIngredient?.ingredientId"
    :data="ferryFiltersStore.filters"
    :header-text="$t('filter.preferences')"
    @close="tripOverviewPanelsStore.closeFerryFiltersPanel"
    @apply-all-filters="tripOverviewPanelsStore.confirmFerryFiltersPanel"
    @reset-all-filters="ferryFiltersStore.resetAllFilters"
  />
</template>
